import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { ArrowRight, Users, Award, GraduationCap, Monitor } from 'lucide-react';
import QuickLinksBar from '../layout/QuickLinksBar';
import { motion } from 'framer-motion';

const VIDEO_1 = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/lu87otz0_iStock-1498333667.mp4';
const VIDEO_2 = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/hzlcmahx_iStock-1466973205.mp4';

export default function HeroSection() {
  const { t, language } = useLanguage();
  const [activeVideo, setActiveVideo] = React.useState(0);
  const videos = [VIDEO_1, VIDEO_2];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveVideo((prev) => (prev === 0 ? 1 : 0));
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const proofItems = [
    { icon: Users, value: t.hero.proof1, sub: t.hero.proof1Sub },
    { icon: Award, value: t.hero.proof2, sub: t.hero.proof2Sub },
    { icon: GraduationCap, value: t.hero.proof3, sub: t.hero.proof3Sub },
    { icon: Monitor, value: t.hero.proof4, sub: t.hero.proof4Sub },
  ];

  return (
    <>
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center"
    >
      {/* Background Video Carousel */}
      <div className="absolute inset-0">
        {videos.map((src, index) => (
          <video
            key={index}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out"
            style={{ opacity: activeVideo === index ? 1 : 0 }}
          >
            <source src={src} type="video/mp4" />
          </video>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#14274B]/80 via-[#14274B]/55 to-[#14274B]/25" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <div className="max-w-3xl">
          {/* Overline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[#66CA9D] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-6"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.hero.overline}
          </motion.p>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-tight tracking-tight mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.hero.title}
          </motion.h1>

          {/* Tagline */}
          {t.hero.titleHighlight && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-snug mb-6"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {t.hero.titleHighlight}
              <br />
              {t.hero.titleHighlight2}
              <br />
              {t.hero.titleHighlight3}
            </motion.p>
          )}

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl sm:text-2xl text-white leading-relaxed mb-10 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.hero.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="hero-cta-primary"
                size="lg"
                className="bg-[#2D78F6] text-white hover:bg-white hover:text-[#14274B] px-8 py-6 text-base font-medium transition-all duration-300 group"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <Link to="/programs">
              <Button
                data-testid="hero-cta-secondary"
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white hover:text-[#14274B] px-8 py-6 text-base font-medium transition-all duration-300"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t.hero.secondaryCta}
              </Button>
            </Link>
            <Link to="/events">
              <Button
                data-testid="hero-cta-events"
                size="lg"
                variant="outline"
                className="border-[#F8CD46]/50 text-[#F8CD46] hover:bg-[#F8CD46] hover:text-[#14274B] px-8 py-6 text-base font-medium transition-all duration-300"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr' ? 'Portes ouvertes virtuelles' : 'Virtual Open House'}
              </Button>
            </Link>
          </motion.div>

          {/* Proof Points */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-8 md:gap-12"
          >
            {proofItems.map((item, index) => (
              <div
                key={index}
                data-testid={`hero-proof-${index}`}
                className="flex items-center gap-3"
              >
                <div className="w-12 h-12 rounded-none bg-white/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#2D78F6]" />
                </div>
                <div>
                  <p
                    className="text-white font-semibold text-lg"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {item.value}
                  </p>
                  <p
                    className="text-white/60 text-sm"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {item.sub}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>

    <QuickLinksBar />
    </>
  );
}
